import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface PurchasingReportItem {
  id: string;
  partNumber: string;
  description: string;
  purchasingGroup: string;
  quantityOnHand: number;
  reorderPoint: number;
  leadTimeDays: number;
  unitCost: number;
  consumed30d: number;
  dailyConsumptionRate: number;
  quotedConsumption: number;
  productionConsumption: number;
  projectedRunoutDays: number | null;
  projectedRunoutDate: string | null;
  recommendedOrderQty: number;
  recommendedReorderDate: string | null;
  status: 'critical' | 'low' | 'adequate' | 'overstocked';
  supplierName: string | null;
}

interface PurchasingGroupSummary {
  purchasingGroup: string;
  totalItems: number;
  totalValue: number;
  lowStockItems: number;
  criticalItems: number;
  items: PurchasingReportItem[];
}

interface PurchasingReport {
  summary: {
    totalInventoryValue: number;
    totalItems: number;
    totalLowStockItems: number;
    totalCriticalItems: number;
    purchasingGroupsCount: number;
  };
  byPurchasingGroup: PurchasingGroupSummary[];
  criticalItems: PurchasingReportItem[];
}

const PURCHASING_GROUPS = [
  { value: '', label: 'All Groups' },
  { value: 'extrusions', label: 'Extrusions' },
  { value: 'injection_molded', label: 'Injection Molded' },
  { value: 'screws_fasteners', label: 'Screws & Fasteners' },
  { value: 'motors_electronics', label: 'Motors & Electronics' },
  { value: 'fabric', label: 'Fabric' },
  { value: 'springs', label: 'Springs' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'hardware', label: 'Hardware' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'critical':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'low':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'adequate':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'overstocked':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

function getRunoutColor(days: number | null): string {
  if (days === null) return 'text-gray-500';
  if (days < 14) return 'text-red-600 dark:text-red-400 font-bold';
  if (days < 28) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

export default function PurchasingReportPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<PurchasingReport | null>(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);

  useEffect(() => {
    loadReport();
  }, [selectedGroup]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.reports.getPurchasingReport(selectedGroup || undefined);
      setReport(data);
    } catch (err) {
      setError('Failed to load purchasing report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const exportToCsv = () => {
    if (!report) return;

    const rows = [
      ['Part Number', 'Description', 'Purchasing Group', 'Qty On Hand', 'Reorder Point',
       'Lead Time (days)', 'Unit Cost', '30d Consumption', 'Daily Rate',
       'Projected Runout', 'Recommended Order Qty', 'Status', 'Supplier'].join(','),
    ];

    report.byPurchasingGroup.forEach(group => {
      group.items.forEach(item => {
        rows.push([
          `"${item.partNumber}"`,
          `"${item.description || ''}"`,
          `"${item.purchasingGroup}"`,
          item.quantityOnHand.toString(),
          item.reorderPoint.toString(),
          item.leadTimeDays.toString(),
          item.unitCost.toFixed(2),
          item.consumed30d.toFixed(2),
          item.dailyConsumptionRate.toFixed(2),
          item.projectedRunoutDate || 'N/A',
          item.recommendedOrderQty.toString(),
          item.status,
          `"${item.supplierName || ''}"`,
        ].join(','));
      });
    });

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchasing-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={loadReport}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Purchasing Report"
        description="Inventory purchasing projections and reorder recommendations"
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Purchasing Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {PURCHASING_GROUPS.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="criticalOnly"
              checked={showCriticalOnly}
              onChange={(e) => setShowCriticalOnly(e.target.checked)}
              className="h-4 w-4 text-yellow-600 rounded border-gray-300"
            />
            <label htmlFor="criticalOnly" className="text-sm text-gray-700 dark:text-gray-300">
              Show Critical Only
            </label>
          </div>

          <div className="flex-1" />

          <button
            onClick={exportToCsv}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>

          <button
            onClick={loadReport}
            className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </Card>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Inventory Value</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(report.summary.totalInventoryValue)}
            </p>
          </Card>
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Items</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {report.summary.totalItems}
            </p>
          </Card>
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Purchasing Groups</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {report.summary.purchasingGroupsCount}
            </p>
          </Card>
          <Card className="text-center bg-yellow-50 dark:bg-yellow-900/20">
            <p className="text-sm text-yellow-600 dark:text-yellow-400">Low Stock Items</p>
            <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
              {report.summary.totalLowStockItems}
            </p>
          </Card>
          <Card className="text-center bg-red-50 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">Critical Items</p>
            <p className="text-2xl font-bold text-red-700 dark:text-red-300">
              {report.summary.totalCriticalItems}
            </p>
          </Card>
        </div>
      )}

      {/* Critical Items Alert */}
      {report && report.criticalItems.length > 0 && (
        <Card className="border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
          <h3 className="text-lg font-bold text-red-700 dark:text-red-300 mb-4">
            Critical Items - Order Now!
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-red-200 dark:border-red-700">
                  <th className="text-left py-2 px-3">Part Number</th>
                  <th className="text-left py-2 px-3">Description</th>
                  <th className="text-right py-2 px-3">Qty On Hand</th>
                  <th className="text-right py-2 px-3">Runout Days</th>
                  <th className="text-right py-2 px-3">Order Qty</th>
                  <th className="text-left py-2 px-3">Supplier</th>
                </tr>
              </thead>
              <tbody>
                {report.criticalItems.slice(0, 10).map(item => (
                  <tr key={item.id} className="border-b border-red-100 dark:border-red-800">
                    <td className="py-2 px-3 font-mono font-medium">{item.partNumber}</td>
                    <td className="py-2 px-3">{item.description}</td>
                    <td className="py-2 px-3 text-right">{formatNumber(item.quantityOnHand)}</td>
                    <td className={`py-2 px-3 text-right ${getRunoutColor(item.projectedRunoutDays)}`}>
                      {item.projectedRunoutDays ?? 'N/A'}
                    </td>
                    <td className="py-2 px-3 text-right font-bold">{formatNumber(item.recommendedOrderQty)}</td>
                    <td className="py-2 px-3">{item.supplierName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* By Purchasing Group */}
      {report && report.byPurchasingGroup
        .filter(g => !showCriticalOnly || g.criticalItems > 0 || g.lowStockItems > 0)
        .map(group => (
        <Card key={group.purchasingGroup}>
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => toggleGroup(group.purchasingGroup)}
          >
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-bold capitalize">
                {group.purchasingGroup.replace(/_/g, ' ')}
              </h3>
              <span className="text-sm text-gray-500">
                {group.totalItems} items | {formatCurrency(group.totalValue)}
              </span>
              {group.criticalItems > 0 && (
                <span className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                  {group.criticalItems} critical
                </span>
              )}
              {group.lowStockItems > 0 && (
                <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">
                  {group.lowStockItems} low
                </span>
              )}
            </div>
            <svg
              className={`w-5 h-5 transition-transform ${expandedGroups.has(group.purchasingGroup) ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {expandedGroups.has(group.purchasingGroup) && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left py-2 px-3">Part Number</th>
                    <th className="text-left py-2 px-3">Description</th>
                    <th className="text-right py-2 px-3">On Hand</th>
                    <th className="text-right py-2 px-3">Reorder Pt</th>
                    <th className="text-right py-2 px-3">Daily Rate</th>
                    <th className="text-right py-2 px-3">Runout Days</th>
                    <th className="text-right py-2 px-3">Reorder Date</th>
                    <th className="text-right py-2 px-3">Order Qty</th>
                    <th className="text-center py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items
                    .filter(item => !showCriticalOnly || item.status === 'critical' || item.status === 'low')
                    .map(item => (
                    <tr key={item.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 px-3 font-mono">{item.partNumber}</td>
                      <td className="py-2 px-3">{item.description}</td>
                      <td className="py-2 px-3 text-right">{formatNumber(item.quantityOnHand)}</td>
                      <td className="py-2 px-3 text-right">{formatNumber(item.reorderPoint)}</td>
                      <td className="py-2 px-3 text-right">{formatNumber(item.dailyConsumptionRate)}</td>
                      <td className={`py-2 px-3 text-right ${getRunoutColor(item.projectedRunoutDays)}`}>
                        {item.projectedRunoutDays ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-right">{item.recommendedReorderDate || '-'}</td>
                      <td className="py-2 px-3 text-right font-bold">{item.recommendedOrderQty > 0 ? formatNumber(item.recommendedOrderQty) : '-'}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
